import React from 'react';
import { useAuth } from '../context/AuthContext';
import { hasFeature, getRequiredTier } from '../config/features';
import UpgradePrompt from './UpgradePrompt';

/**
 * FeatureGate - Conditionally render content based on user's subscription tier
 *
 * @param {string} feature - The feature key to check (from features.js)
 * @param {React.ReactNode} children - Content to render if user has access
 * @param {React.ReactNode} fallback - Optional custom fallback if user doesn't have access
 * @param {string} promptVariant - 'card' (default), 'inline', 'banner', or 'hidden'
 *
 * Usage:
 *   <FeatureGate feature="expense_tracking">
 *     <ExpenseTracker />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="square_integration" promptVariant="banner">
 *     <SquareSettings />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="advanced_reports" fallback={<BasicReports />}>
 *     <AdvancedReports />
 *   </FeatureGate>
 */
function FeatureGate({ feature, children, fallback, promptVariant = 'card', lapsedMayRead = false }) {
  // effectiveTier rather than user.subscriptionTier: it folds in the
  // subscription status, so a cancelled plan keeps its features until the paid
  // period ends and then stops. See resolveEffectiveTier in config/features.js.
  const { effectiveTier, isLapsed } = useAuth();

  // lapsedMayRead: a lapsed company still sees its own data on a read page
  // (Expenses, Reports). Its tier has usually been reset to free, so the tier
  // cannot decide this; the server lets the same reads through and refuses
  // the writes with SUBSCRIPTION_LAPSED.
  const canAccess = hasFeature(effectiveTier, feature) || (lapsedMayRead && isLapsed());

  if (canAccess) {
    return <>{children}</>;
  }

  // User doesn't have access - show fallback or upgrade prompt
  if (fallback) {
    return <>{fallback}</>;
  }

  // If promptVariant is 'hidden', render nothing
  if (promptVariant === 'hidden') {
    return null;
  }

  // Show upgrade prompt
  const requiredTier = getRequiredTier(feature);
  return <UpgradePrompt feature={feature} requiredTier={requiredTier} variant={promptVariant} />;
}

/**
 * useFeatureAccess - Hook to check feature access in components
 *
 * Usage:
 *   const { canAccess, requiredTier } = useFeatureAccess('expense_tracking');
 *   if (canAccess) { ... }
 */
export function useFeatureAccess(feature) {
  const { effectiveTier } = useAuth();
  const canAccess = hasFeature(effectiveTier, feature);
  const requiredTier = getRequiredTier(feature);

  // userTier is kept as the key for call-site compatibility, but it is the
  // effective tier - what the company can use today, not what it bought.
  return { canAccess, requiredTier, userTier: effectiveTier };
}

export default FeatureGate;
